import { Container, Truck } from 'lucide-react';
import type { CartItem } from '../types';
import { calcItemLoads } from '~/utils/order';
import { useTranslation } from 'react-i18next';

interface TransportationSectionProps {
  items: CartItem[];
}

export function TransportationSection({
  items,
}: Readonly<TransportationSectionProps>) {
  const { t } = useTranslation('cart');
  const { truckLoads, railcarLoads } = items.reduce(
    (acc, item) => {
      const loads = calcItemLoads(item);
      return {
        truckLoads: acc.truckLoads + loads.truckLoads,
        railcarLoads: acc.railcarLoads + loads.railcarLoads,
      };
    },
    { truckLoads: 0, railcarLoads: 0 }
  );

  const truckCount = Math.ceil(truckLoads);
  const railcarCount = Math.ceil(railcarLoads);
  const lastTruckPct =
    truckLoads > 0 ? Math.round((truckLoads % 1 || 1) * 100) : 0;
  const lastRailcarPct =
    railcarLoads > 0 ? Math.round((railcarLoads % 1 || 1) * 100) : 0;

  return (
    <div className="bg-neut-bg rounded-lg border border-neut-200 p-6 shadow-sm">
      <div className="flex items-center gap-2 mb-4">
        <Truck className="w-5 h-5 text-success-500" aria-hidden="true" />
        <h2 className="text-neut-prim font-semibold">
          {t('cart.transportation_estimate')}
        </h2>
      </div>
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-status-info-50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Truck className="w-4 h-4 text-status-info-600" />
            <span className="text-xs text-neut-600 font-normal">
              {t('cart.trucks')}
            </span>
          </div>
          <div className="flex items-baseline gap-1 mb-1">
            <span className="text-2xl text-status-info-700 font-semibold">
              {truckCount}
            </span>
            <span className="text-xs text-status-info-600 font-normal">
              {t('cart.trucks_lowercase')}
            </span>
          </div>
          <p className="text-xs text-neut-500">
            {truckLoads.toFixed(2)} {t('cart.loads')}
          </p>
          {truckLoads > 0 && (
            <p className="text-xs text-status-info-600 mt-1">
              {t('cart.last_truck')}: {lastTruckPct}% {t('cart.full')}
            </p>
          )}
        </div>
        <div className="bg-warning-50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Container className="w-4 h-4 text-warning-600" />
            <span className="text-xs text-neut-600 font-normal">
              {t('cart.railcars')}
            </span>
          </div>
          <div className="flex items-baseline gap-1 mb-1">
            <span className="text-2xl text-warning-700 font-semibold">
              {railcarCount}
            </span>
            <span className="text-xs text-warning-600 font-normal">
              {t('cart.railcars_lowercase')}
            </span>
          </div>
          <p className="text-xs text-neut-500">
            {railcarLoads.toFixed(2)} {t('cart.loads')}
          </p>
          {railcarLoads > 0 && (
            <p className="text-xs text-warning-600 mt-1">
              {t('cart.last_railcar')}: {lastRailcarPct}% {t('cart.full')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
